/**
 * Defensive dedup for the tracker's designation pickers.
 *
 * Dev seed scripts historically attached catalog-derived designations
 * with random doc ids on every run, so a project could accumulate
 * several identical copies of the same designation (see the
 * 2026-06-22 designation-dedup spec). This is a pure safety net: it
 * collapses such duplicates before they reach the picker, which also
 * removes the React "two children with the same key" warning since
 * options become unique by construction.
 *
 * Dedup key (mirrors the `taken` set in `buildDesignationOptions`):
 *   - `${catalogId}::${variant ?? ''}` when `catalogId` is present,
 *   - else the normalized `name` (accent-fold + lowercase + collapsed
 *     whitespace, via `foldText`).
 *
 * Stable: the first occurrence wins; input order is otherwise
 * preserved. Pure — no React, no Firestore. Unit-tested.
 */

import type { Designation } from '@altyor-kronos/shared-types';
import { foldText } from './entryFilter';

function dedupeKey(d: Designation): string {
  if (d.catalogId) {
    return `cat:${d.catalogId}::${d.variant ?? ''}`;
  }
  // Custom (no catalog link): normalize the name so accent/case/whitespace
  // variants collapse to one entry.
  return `name:${foldText(d.name).trim().replace(/\s+/g, ' ')}`;
}

export function dedupeDesignations(
  designations: readonly Designation[],
): Designation[] {
  const seen = new Set<string>();
  const result: Designation[] = [];
  for (const d of designations) {
    const key = dedupeKey(d);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(d);
  }
  return result;
}
